import { useCallback } from 'react';
import moment from 'moment';
import { httpClient } from '@s20/services/httpClient';
import { ApiRoutes } from '../../../../../Common';
import { Modal } from '../../../../../common/Modal';
import {
    getCorrectTime, ConfirmationStatus, getJobTimeZone, JobAssignmentCancellationType,
} from '../../../../../dispatch/Dispatch';
import { toasty } from '../../../../../common/forms/FormElements';
import { CancelJobBody } from './CancelJobBody';

export const useCancelJob = (options) => {
    const {
        day, job, jobCancellationTypes, jobCancellationReasons,
        onClose,
        //jobAssignmentId,
    } = options || {};

    const handleCancelJob = useCallback(() => {
        const selectedDayJobToRemove = { day, job };

        Modal.show({
            onClose,
            title: 'Cancel Job',
            initialValues: {
                selectedJobCancellationTime: getCorrectTime(selectedDayJobToRemove, new Date()),
            },
            SaveButtonProps: {
                color: 'danger',
                label: 'Cancel Job',
                savingLabel: 'Cancelling...',
            },
            onSubmit: async ({
                selectedJobCancellationType,
                selectedJobCancellationReasonType,
                selectedJobCancellationNotes,
                selectedJobCancellationTime,
                cancelContactId,
                isCrewOnSiteAndWorkPerformed,
            }) => {
                const jobCancelTime = moment.tz(selectedJobCancellationTime, getJobTimeZone(selectedDayJobToRemove)).format();

                // if its client cancel need to make sure there are assignments as a timesheet gets made
                if (selectedJobCancellationType === JobAssignmentCancellationType.ClientCancel) {
                    const { assignments } = job;
                    const dayAssignments = assignments[day];

                    // If no emps or equipment, no need to open editor.
                    if (
                        !(dayAssignments.staff ?? []).length
                        && !(dayAssignments.equipment ?? []).length
                    ) {
                        toasty.error(
                            'Please make an assignment to the job on this day for client cancellations.',
                        );
                        return false;
                    }

                    // Need at least one emp to make status edits. `assignment` is the true signal
                    // that an employee is actually assigned.
                    if (
                        !(dayAssignments.staff ?? []).filter((x) => x.assignment)
                            .length
                    ) {
                        toasty.error(
                            'Please assign at least one employee to the job on this day for client cancellations.',
                        );
                        return false;
                    }

                    // Make sure no equipment is unassigned.
                    if (
                        (dayAssignments.equipment ?? []).filter(
                            (x) => !x.assignedEquipment?.id && !x.assignedEmployee?.id,
                        ).length
                    ) {
                        toasty.error(
                            'Please ensure all job equipment has been assigned on this day for client cancellations.',
                        );
                        return false;
                    }
                }

                const model = {
                    jobId: job.assignments?.[day]?.jobId || job.id,
                    day,
                    cancellationTypeId: selectedJobCancellationType,
                    cancellationReasonTypeId: selectedJobCancellationReasonType,
                    cancellationNote: selectedJobCancellationNotes,
                    cancellationTime: jobCancelTime,
                    sendNotifications: true,
                    cancelContactId,
                    isCrewOnSiteAndWorkPerformed,
                };

                const response = await httpClient.post(
                    ApiRoutes.dispatch.cancelJob(),
                    model,
                );

                if (response === 'ok') {
                    toasty.success('Notification sent for cancellation');
                } else {
                    toasty.error('Notification Error');
                }
            },
            body: () => (
                <CancelJobBody
                    job={job}
                    day={day}
                    jobCancellationReasons={jobCancellationReasons}
                    jobCancellationTypes={jobCancellationTypes}
                />
            ),
        });
    }, [onClose, job, day, jobCancellationReasons, jobCancellationTypes]);

    const handleRemoveDayJob = useCallback(() => {
        Modal.show({
            onClose,
            onSubmit: async () => {
                const dayAssignment = job.assignments[day];

                if (
                    dayAssignment.staff.length > 0
                    || dayAssignment.equipment.length > 0
                ) {
                    const empAssignments = dayAssignment.staff.filter(
                        (x) => x.assignment !== null,
                    );

                    const eqAssignments = dayAssignment.equipment.filter(
                        (x) => x.assignment !== null,
                    );

                    const allAssignments = [...empAssignments, ...eqAssignments];

                    if (allAssignments.length) {
                        // any emp or eq assignments have a confirmation less than 3 can be removed without cancel.
                        // Could be an issue here if they get into a situation where there is a mix/match? not sure its possible.
                        if (
                            allAssignments.filter(
                                (x) => x.assignment.confirmationStatusId < ConfirmationStatus.NoResponse,
                            ).length == allAssignments.length
                        ) {
                            // just remove them all as they are not confirmed or anything
                            await httpClient.post(ApiRoutes.dispatch.removeAllAssignments(),
                                allAssignments.map((x) => x.jobAssignmentId),
                            );
                        } else {
                            // needs cancellation
                            handleCancelJob();

                            return { keepOpen: true };
                        }
                    }
                }
            },
            title: 'Cancel Job',
            body: () => 'Are you sure you want to cancel this job?',
            SaveButtonProps: {
                label: 'Yes, cancel it',
                savingLabel: 'Cancelling',
                color: 'danger',
            },
        });
    }, [day, job, handleCancelJob, onClose]);

    return handleRemoveDayJob;
};